import * as THREE from 'three/webgpu';
import type { PoseSnapshot } from './interp';

export type CameraMode = 'chase-near' | 'chase-far' | 'bonnet' | 'bumper' | 'cockpit';
export const CAMERA_MODES: CameraMode[] = ['chase-near', 'chase-far', 'cockpit', 'bonnet', 'bumper'];

export interface CameraSettings {
  fov: Record<CameraMode, number>;
  reduceMotion: boolean;
}

export const DEFAULT_FOV: Record<CameraMode, number> = {
  'chase-near': 62,
  'chase-far': 58,
  bonnet: 68,
  bumper: 72,
  cockpit: 72,
};

export interface CameraInputs {
  lookYaw: number;
  lookPitch: number;
  lookBack: boolean;
  /** Vehicle accelerations in g (car frame) for head motion. */
  gLong: number;
  gLat: number;
  gVert: number;
  speed: number;
}

/** Ground/obstacle query for keeping the chase camera out of terrain and trees. */
export interface CameraCollision {
  heightAt(x: number, z: number): number;
  /** Fraction (0..1) along from→to of the first static obstacle, or 1 if clear. */
  obstruction?(fx: number, fy: number, fz: number, tx: number, ty: number, tz: number): number;
}

/** Car-local camera mounts (+Z forward, +X left). Cockpit is the left-hand driver seat. */
export interface CameraMounts {
  cockpit: [number, number, number];
  bonnet: [number, number, number];
  bumper: [number, number, number];
}

export const DEFAULT_MOUNTS: CameraMounts = {
  cockpit: [0.36, 1.13, -0.22],
  bonnet: [0, 1.2, 0.75],
  bumper: [0, 0.55, 2.12],
};

const _q = new THREE.Quaternion();
const _v = new THREE.Vector3();
const _w = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _up = new THREE.Vector3();
const _e = new THREE.Euler();
const _m = new THREE.Matrix4();
const _p = new THREE.Vector3();
const _look = new THREE.Vector3();
const _off = new THREE.Vector3();
/** Showcase (menu / results) orbit: radius, height and angular speed. */
const ORBIT = { along: 7.8, across: 4.2, height: 1.3, speed: 0.09, fov: 40 };

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  mode: CameraMode = 'chase-near';
  settings: CameraSettings = { fov: { ...DEFAULT_FOV }, reduceMotion: false };
  mounts: CameraMounts = DEFAULT_MOUNTS;
  private chasePos = new THREE.Vector3();
  private chaseVel = new THREE.Vector3();
  private chaseInit = false;
  private lookYaw = 0;
  private lookPitch = 0;
  private headOffset = new THREE.Vector3();
  private headVel = new THREE.Vector3();
  private smoothedHeading = new THREE.Vector3(0, 0, 1);
  /** Menu and results: a slow orbit around the car instead of the driving camera. */
  showcase = false;
  private orbitT = 0;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(DEFAULT_FOV['chase-near'], aspect, 0.05, 6000);
  }

  setMode(m: CameraMode): void {
    this.mode = m;
    this.chaseInit = false;
  }

  cycle(): CameraMode {
    const i = CAMERA_MODES.indexOf(this.mode);
    this.setMode(CAMERA_MODES[(i + 1) % CAMERA_MODES.length]);
    return this.mode;
  }

  reset(): void {
    this.chaseInit = false;
  }

  get interior(): boolean {
    return this.mode === 'cockpit';
  }

  update(s: PoseSnapshot, inp: CameraInputs, dt: number, col: CameraCollision): void {
    const cam = this.camera;
    if (this.showcase) {
      this.updateOrbit(s, dt, col);
      return;
    }
    const fov = this.settings.fov[this.mode];
    if (cam.fov !== fov) {
      cam.fov = fov;
      cam.updateProjectionMatrix();
    }
    _q.set(s.qx, s.qy, s.qz, s.qw);
    _fwd.set(0, 0, 1).applyQuaternion(_q);
    _up.set(0, 1, 0).applyQuaternion(_q);
    // Look direction smoothing (keys/stick/mouse).
    const targetYaw = inp.lookBack ? Math.PI : inp.lookYaw * (Math.PI / 2) * 0.95;
    const k = 1 - Math.exp(-dt / 0.08);
    this.lookYaw += (targetYaw - this.lookYaw) * k;
    this.lookPitch += (inp.lookPitch * 0.5 - this.lookPitch) * k;

    if (this.mode === 'chase-near' || this.mode === 'chase-far') {
      this.updateChase(s, inp, dt, col);
      return;
    }
    const mount = this.mode === 'cockpit' ? this.mounts.cockpit : this.mode === 'bonnet' ? this.mounts.bonnet : this.mounts.bumper;
    _v.set(mount[0], mount[1], mount[2]);
    if (this.mode === 'cockpit' && !this.settings.reduceMotion) {
      // Head motion: a damped spring driven by g-forces (head lags opposite to acceleration).
      const target = _w.set(-inp.gLat * 0.035, -(inp.gVert - 1) * 0.02, -inp.gLong * 0.03);
      const stiff = 60;
      const damp = 2 * Math.sqrt(stiff) * 0.8;
      this.headVel.addScaledVector(target.sub(this.headOffset).multiplyScalar(stiff).addScaledVector(this.headVel, -damp), dt);
      this.headOffset.addScaledVector(this.headVel, dt);
      this.headOffset.clampLength(0, 0.08);
      _v.add(this.headOffset);
    }
    _v.applyQuaternion(_q).add(_w.set(s.px, s.py, s.pz));
    cam.position.copy(_v);
    cam.quaternion.copy(_q);
    // Camera looks down -Z; car forward is +Z → turn around.
    cam.rotateY(Math.PI + this.lookYaw);
    cam.rotateX(this.lookPitch + (this.mode === 'cockpit' ? -0.06 : -0.03));
    if (this.mode === 'cockpit' && !this.settings.reduceMotion) {
      // Slight horizon stabilisation: real heads level against roll.
      _e.setFromQuaternion(cam.quaternion, 'YXZ');
      _e.z *= 0.6;
      cam.quaternion.setFromEuler(_e);
    }
  }

  private updateOrbit(s: PoseSnapshot, dt: number, col: CameraCollision): void {
    const cam = this.camera;
    if (cam.fov !== ORBIT.fov) {
      cam.fov = ORBIT.fov;
      cam.updateProjectionMatrix();
    }
    this.orbitT += dt;
    _q.set(s.qx, s.qy, s.qz, s.qw);
    _fwd.set(0, 0, 1).applyQuaternion(_q);
    // An ellipse stretched along the car (and so along the road it sits on, clear of the tree line),
    // starting at the front three-quarter view and drifting slowly round.
    const a = 0.75 + this.orbitT * ORBIT.speed;
    const hl = Math.hypot(_fwd.x, _fwd.z) || 1;
    const fx = _fwd.x / hl;
    const fz = _fwd.z / hl;
    const ca = Math.cos(a) * ORBIT.along;
    const sa = Math.sin(a) * ORBIT.across;
    _look.set(s.px, s.py + 0.5, s.pz);
    _p.set(s.px + fx * ca + fz * sa, s.py + ORBIT.height + 0.25 * Math.sin(this.orbitT * 0.13), s.pz + fz * ca - fx * sa);
    if (col.obstruction) {
      const f = col.obstruction(_look.x, _look.y, _look.z, _p.x, _p.y, _p.z);
      if (f < 1) _p.sub(_look).multiplyScalar(Math.max(0.35, f - 0.05)).add(_look);
    }
    const ground = col.heightAt(_p.x, _p.z);
    if (_p.y < ground + 0.5) _p.y = ground + 0.5;
    cam.position.copy(_p);
    _m.lookAt(cam.position, _look, _up.set(0, 1, 0));
    cam.quaternion.setFromRotationMatrix(_m);
  }

  private updateChase(s: PoseSnapshot, inp: CameraInputs, dt: number, col: CameraCollision): void {
    const cam = this.camera;
    const far = this.mode === 'chase-far';
    const dist = far ? 8.2 : 5.4;
    const height = far ? 2.7 : 1.85;
    const carPos = _w.set(s.px, s.py, s.pz);
    // Heading follows the car's forward direction projected on the ground, smoothed so slides
    // show the car's angle.
    const hx = _fwd.x;
    const hz = _fwd.z;
    const hl = Math.hypot(hx, hz) || 1;
    const kh = 1 - Math.exp(-dt / 0.22);
    this.smoothedHeading.x += (hx / hl - this.smoothedHeading.x) * kh;
    this.smoothedHeading.z += (hz / hl - this.smoothedHeading.z) * kh;
    this.smoothedHeading.y = 0;
    this.smoothedHeading.normalize();
    // Apply look yaw around the car.
    const cy = Math.cos(this.lookYaw);
    const sy = Math.sin(this.lookYaw);
    const dx = this.smoothedHeading.x * cy + this.smoothedHeading.z * sy;
    const dz = -this.smoothedHeading.x * sy + this.smoothedHeading.z * cy;
    const target = _v.set(carPos.x - dx * dist, carPos.y + height, carPos.z - dz * dist);
    if (!this.chaseInit) {
      this.chasePos.copy(target);
      this.chaseVel.set(0, 0, 0);
      this.chaseInit = true;
    }
    // Critically damped spring toward the target.
    const w = far ? 7 : 9;
    const ax = (target.x - this.chasePos.x) * w * w - this.chaseVel.x * 2 * w;
    const ay = (target.y - this.chasePos.y) * w * w - this.chaseVel.y * 2 * w;
    const az = (target.z - this.chasePos.z) * w * w - this.chaseVel.z * 2 * w;
    this.chaseVel.x += ax * dt;
    this.chaseVel.y += ay * dt;
    this.chaseVel.z += az * dt;
    this.chasePos.addScaledVector(this.chaseVel, dt);
    // Keep the camera at a fixed-ish distance (no lag-stretch at speed).
    const off = _off.copy(this.chasePos).sub(carPos);
    const horiz = Math.hypot(off.x, off.z);
    const maxH = dist * 1.25;
    if (horiz > maxH) {
      off.x *= maxH / horiz;
      off.z *= maxH / horiz;
      this.chasePos.set(carPos.x + off.x, this.chasePos.y, carPos.z + off.z);
    }
    // Obstruction (trees, rocks, banks): pull the camera in toward the car.
    const look = _look.set(carPos.x, carPos.y + 1.0, carPos.z);
    const p = _p.copy(this.chasePos);
    if (col.obstruction) {
      const f = col.obstruction(look.x, look.y, look.z, p.x, p.y, p.z);
      if (f < 1) p.sub(look).multiplyScalar(Math.max(0.15, f - 0.08)).add(look);
    }
    // Never below the terrain.
    const ground = col.heightAt(p.x, p.z);
    if (p.y < ground + 0.6) p.y = ground + 0.6;
    cam.position.copy(p);
    _m.lookAt(cam.position, look.add(_up.set(0, 0.2, 0)), _up.set(0, 1, 0));
    cam.quaternion.setFromRotationMatrix(_m);
    cam.rotateX(this.lookPitch);
    void inp;
  }
}
